/**
 * "Worth doing next" — the panel that answers the first question a marketer
 * asks, which this screen used not to answer at all.
 *
 * WHY THE GRID IS EXPORTED SEPARATELY FROM THE CARD
 * -----------------------------------------------------------------------------
 * {@link OpportunityGrid} takes an array and nothing else; {@link
 * OpportunitiesCard} takes the async read and owns the retry and the
 * render-nothing decisions.
 *
 * An earlier version of this paragraph justified the split with a config-wall
 * story — that a suite could not mount the card because its import closure
 * reached `lib/config.ts`. **That was false in three separate ways**, and it is
 * recorded here because a wrong comment outlives the code it describes: both
 * exports live in one module and therefore share one closure by construction;
 * this file imports no API client and no `lib/format`; and `verify-dashboard`
 * mounts the whole card, exactly as it already does for `ActivityCharts` and
 * `EngagementCard`, which take the same `AsyncData` prop.
 *
 * **Slice 50 narrowed the middle clause rather than deleting it.** This file now
 * reaches `lib/api/overview` — but only inside the save handler, through a
 * dynamic `await import`, so the module's import closure at load time is still
 * free of `lib/config.ts` and the card is still mountable by the two suites that
 * assert everything on it. That is the same trade `CalendarCard`'s `onAdd`
 * makes, for the same reason and at the same cost: one fetch the first time
 * somebody presses, long after the app has booted.
 *
 * The real reason is smaller and still worth it: the grid is the half that
 * decides what words appear, so a probe can check copy without building an
 * async state to do it.
 *
 * SLICE 50: ONE OF THESE TILES NOW WRITES, AND THE SPLIT IS WHAT MAKES IT
 * ASSERTABLE
 * -----------------------------------------------------------------------------
 * Three of the five counts are populations of people, and the API now sends
 * those tiles `action: { kind: 'save' }` instead of a link. The tile draws a
 * `Button` for a save and a `Link` for a link — the same size either way,
 * because the `Link` already wears `buttonVariants({ variant: 'secondary', size:
 * 'sm' })`, so the grid does not move between the two states.
 *
 * **Which of the two appears is the server's decision, never this file's.** It
 * ran the count, so it is the only thing that knows whether a count is a
 * population of people and whether a cohort of that kind is already saved and
 * still current. This file holds no list of saveable kinds, exactly as it holds
 * no opinion about `OPPORTUNITY_ORDER`; a kind that cannot be saved is refused
 * at the edge and the refusal is drawn in the failure line.
 *
 * The async state is split so it can be rendered by a probe rather than
 * described by one: `OpportunityGrid` takes `savingKinds`, so a busy tile is a
 * render, and {@link CohortSaveNote} takes the outcome, so the success and the
 * failure lines are renders too. `renderToStaticMarkup` runs no handlers, so a
 * card that owned all three privately would have its three most interesting
 * states covered by nothing but a grep.
 *
 * **And the press itself is out here too, for the same reason carried one step
 * further.** Three rendered states plus a source search over the wiring between
 * them is not the press: the search passes on a `reload()` in the wrong branch
 * and on two of them left behind after the call they belonged to moved.
 * {@link pressCohortSave} takes the setters and the reload and is an ordinary
 * async function, so `verify-dashboard` seeds `lib/config.ts`, stubs `fetch`, and
 * asserts what the real chain did — which request left, in what order the busy
 * flag went on and off, which sentence the refusal carried, and that the re-read
 * happened on both paths.
 *
 * NO TOAST, AND THE REASON IS THE SAME ONE `CalendarCard` RECORDS
 * -----------------------------------------------------------------------------
 * `useToast` throws without a `ToastProvider`, and `verify-dashboard` mounts
 * this grid bare. A toast would make the verdict of a save unassertable in the
 * one suite that holds every assertion about this card. Both outcomes are
 * inline, in ONE slot under the grid: a success and a failure can therefore
 * never be on screen contradicting each other, and a screen reader gets one
 * live region per card rather than four announcing from wherever focus happens
 * to be.
 *
 * The API client is imported inside the handler, not at the top, for the reason
 * `CalendarCard`'s `onAdd` states in full: `lib/api/overview` reaches
 * `lib/config.ts`, which reads `import.meta.env`, which does not exist under
 * `renderToStaticMarkup`. A static import would make this module unmountable by
 * the two suites that assert everything on it.
 *
 * NOTHING RENDERS UNTIL THERE IS SOMETHING TO SAY
 * -----------------------------------------------------------------------------
 * `opportunities: []` is a success — it is what a workspace with nobody dormant,
 * nobody unmessaged, every template used and every segment sent to should get —
 * and it renders `null`. **So does the first load**, and that is a fix rather
 * than an oversight. The first version drew the title and a spinner while the
 * read was in flight, so a well-run workspace got "Worth doing next / Looking at
 * your lists…" in the second slot on every load, then watched the card vanish
 * and the page jump up by a card's height. A panel that is usually absent must
 * not reserve space before it knows it has any.
 *
 * A **failed** read is the one case that draws without tiles: it renders a
 * retry. Silence there would be indistinguishable from "all clear", which is the
 * confusion `AttentionSlot` was rewritten to avoid.
 *
 * COLOUR IS NOT USED HERE, AND THAT IS THE POINT
 * -----------------------------------------------------------------------------
 * The chips are all `neutral`. The first version tinted them by urgency — `warn`
 * for a dormant audience, `info` for people never reached — and both collide
 * with a fixed meaning **on this same page, in the same pill**:
 *
 *   - `warn` is `BAND_VERDICT`'s "Climbing towards the line" on
 *     `SendingHealthTiles`, mounted directly below this card. One amber pill
 *     would have read "you could send more email" and another, a few hundred
 *     pixels later, "your AWS account may be suspended".
 *   - `info` is `StateChip`'s "Projected" — across this product an info pill
 *     over a figure means *this number is assumed, not measured*. These counts
 *     are exact.
 *
 * Urgency is carried by **position** instead, which was already doing it:
 * `OPPORTUNITY_ORDER` ranks people above housekeeping on the server and
 * `OpportunityGrid` deliberately does not re-sort, so top-left is the most worth
 * doing. The chip still names the kind in words, so nothing is lost on a
 * monochrome screen or to a colour-blind reader — there is no colour to lose it
 * to.
 *
 * The tile surface is `bg-white/[0.02]`, which is what every other inner tile on
 * this page uses. `bg-surface-2` is opaque and does not track `Card`'s own
 * gradient, so four tiles in it read heavier and purpler than the bounce-rate
 * tiles directly below them.
 */
import {
  OPPORTUNITY_COPY,
  OPPORTUNITY_SUPPRESSION_NOTE,
  type Opportunity,
  type OpportunitiesResponse,
  type OpportunityKind,
  type SaveCohortResponse,
} from '@wend/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, buttonVariants, cn } from '@wend/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { errorMessage } from '../../lib/api/errors';
import type { AsyncData } from '../../lib/use-async-data';

/**
 * One tile. Every word on it comes from the contract — the chip and the headline
 * built from the count — so the screen cannot describe an opportunity
 * differently from the API that counted it.
 */
const OPPORTUNITY_DOT: Readonly<Record<'good' | 'warn' | 'info' | 'neutral', string>> = {
  good: 'bg-good',
  warn: 'bg-warn',
  info: 'bg-info',
  neutral: 'bg-muted',
};

/**
 * The four words this screen adds, and why they are not in `packages/shared`.
 *
 * Every word a tile prints about an opportunity — the chip, the headline, the
 * button's label — comes from the contract, and every word of a *refusal* comes
 * from the API's own message, which is `COHORT_REFUSALS`. These three describe
 * something the API never observed: what happened in this browser between a
 * press and an answer. There is no server that could send "Saved as", because
 * the server does not know a button was pressed.
 *
 * They are a named constant rather than three literals in the JSX for the reason
 * the contract's copy is a constant: the probe that asserts the failure line
 * names the kind reads the same string the screen renders, so it cannot pass
 * against a sentence that has drifted.
 *
 * The outcome is a *label reporting an outcome*, never an explanation of one.
 * "Saved as" and the name is the whole success report — the tile's control
 * changing from a button to a link is the other half of it, and that costs no
 * words at all.
 */
const COHORT_SAVE_COPY = {
  saved: 'Saved as',
  failed: (label: string) => `${label} — could not save.`,
  /**
   * Up to three buttons on this card read the same three words, so each needs a
   * name of its own. Attribute text is stripped by `visibleBlocks`' tag pass, so
   * it costs nothing against the word ceiling.
   */
  ariaLabel: (label: string) => `Save ${label} as a segment`,
} as const;

/**
 * What a press ended in. One value, so the two outcomes cannot both be drawn.
 *
 * A boolean pair — `saved` beside `failed` — was the first shape, and it makes
 * "Saved as Dormant audience · 26 Sep 2026" and "Dormant audience — could not
 * save." simultaneously renderable. One slot makes that unrepresentable rather
 * than merely avoided by whoever writes the setters next.
 */
export type CohortSaveOutcome =
  | { readonly ok: true; readonly segmentId: string; readonly name: string }
  | { readonly ok: false; readonly kind: OpportunityKind; readonly message: string };

/**
 * The one line under the grid, in the slot that holds either outcome.
 *
 * `ErrorState` is deliberately not used for a failed save, and the reason is its
 * shape rather than its words: it draws a bordered panel with a retry in it, and
 * a failed write leaves four healthy tiles and a pressable button beside it.
 * `ErrorState` stays for what it is for on this card — a READ that came back
 * with nothing.
 *
 * The failure names the kind because up to three identical buttons are on
 * screen and a bare "could not save" would not say which one. The success does
 * NOT name the kind: the button that changed said which, and "Never messaged
 * saved as Never messaged · 26 Sep 2026" says it twice.
 */
export function CohortSaveNote({ outcome }: { outcome: CohortSaveOutcome | null }) {
  if (outcome === null) return null;

  if (!outcome.ok) {
    return (
      <p data-role="cohort-save-failed" role="alert" className="text-xs leading-relaxed text-bad">
        {COHORT_SAVE_COPY.failed(OPPORTUNITY_COPY[outcome.kind].label)} {outcome.message}
      </p>
    );
  }

  return (
    <p data-role="cohort-save-note" role="status" className="text-xs text-muted">
      {COHORT_SAVE_COPY.saved}{' '}
      <Link
        to={`/segments/${encodeURIComponent(outcome.segmentId)}`}
        className="text-text underline underline-offset-4"
      >
        {outcome.name}
      </Link>
    </p>
  );
}

/**
 * One tile, in the shape the approved design draws it: a coloured dot beside a
 * small mono label, the headline, then a row pairing the source of the figure
 * with the action.
 *
 * THE DOT IS A CATEGORY, NOT A VERDICT
 * ---------------------------------------------------------------------------
 * `warn` on the dormant tile means "this is the people-gone-quiet one", not
 * "this is bad". `bad` is never used on this card, and no threshold is drawn —
 * slice 31's rule that a verdict belongs only where a figure is measured
 * against a line it is judged by.
 *
 * WHAT THE DESIGN HAS THAT THIS DOES NOT
 * ---------------------------------------------------------------------------
 * The mockup gives each tile a sentence of explanation — *"A first message
 * inside a week does measurably better than one a month later."* That sentence
 * was in this contract and was removed, deliberately, because it is a claim
 * about marketing that this product has measured nothing to support. It has not
 * been restored. What is printed is what is true: the count, where it came
 * from, and the thing to do about it.
 */
function OpportunityTile({
  opportunity,
  onSave,
  saving,
}: {
  opportunity: Opportunity;
  /**
   * Undefined when the grid was mounted without a card to write through — a
   * probe over the words, and nothing in the app.
   */
  onSave?: (kind: OpportunityKind) => void;
  /** True for the one tile whose own save is in flight. */
  saving: boolean;
}) {
  const copy = OPPORTUNITY_COPY[opportunity.kind];
  const action = opportunity.action;

  return (
    <li
      data-role="opportunity"
      data-kind={opportunity.kind}
      className="flex flex-col gap-2.5 rounded-base border border-line bg-white/[0.02] px-3.5 py-3.5"
    >
      <span className="flex items-center justify-between gap-2">
        <span
          data-role="opportunity-source"
          className="inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.11em] text-muted"
        >
          <span aria-hidden="true" className={cn('size-1.5 rounded-full', OPPORTUNITY_DOT[copy.tone])} />
          {copy.label}
        </span>
        {/*
          SLICE 66: WHAT A SAVE MAKES, SAID BEFORE THE PRESS.
          A tile whose action saves writes a cohort segment, and a cohort is a
          snapshot — the people who match now, counted once, never updated.
          The Segments list calls that kind "Snapshot" in this same badge, so a
          reader meets the word here first and recognises the segment there.
          `ident-a`, the identity colour for a list's kind: not a status, so it
          cannot read as good news or a warning on a card whose order alone
          carries urgency. Link tiles get none — they make nothing.
        */}
        {action.kind === 'save' && (
          <Badge tone="ident-a" data-role="opportunity-snapshot">
            Snapshot
          </Badge>
        )}
      </span>

      <p className="text-sm font-semibold leading-snug text-text">
        {copy.headline(opportunity.count)}
      </p>

      {/*
        ONE SLOT, TWO CONTROLS, THE SAME SIZE. The `Link` wears the button's own
        variants, so a tile whose action is a save is not a different height from
        one whose action is a link, and the grid does not reflow when a press
        turns the first into the second. `loading` grows the control by about an
        icon's width, which the source line's `min-w-0 truncate` absorbs.
      */}
      <span className="mt-auto flex items-center justify-between gap-2.5">
        <span className="min-w-0 truncate font-mono text-[0.66rem] text-muted">{copy.source}</span>
        {action.kind === 'save' ? (
          <Button
            /* Not in a form, but a secondary control that must never submit one. */
            type="button"
            variant="secondary"
            size="sm"
            className="flex-none"
            /*
              The whole busy state, and no words: a spinner in the leading-icon
              slot, `aria-busy`, and `disabled` — so a second press on this tile
              is impossible. The label does NOT become "Saving…"; the spinner
              already says it, and a label that changes mid-press is a second
              string to keep in step. The other tiles stay pressable, because a
              save of a different cohort is an independent write and disabling
              them would be a lie about coupling.
            */
            loading={saving}
            aria-label={COHORT_SAVE_COPY.ariaLabel(copy.label)}
            onClick={() => {
              onSave?.(opportunity.kind);
            }}
          >
            {action.label}
          </Button>
        ) : (
          <Link
            to={action.href}
            className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'flex-none')}
          >
            {action.label}
          </Link>
        )}
      </span>
    </li>
  );
}


/**
 * The tiles, in the order the API sent them.
 *
 * **It does not sort.** `OPPORTUNITY_ORDER` already decided, on the server, that
 * people come before housekeeping, and a second opinion here is a second thing
 * to keep in step with the contract. Renders `null` on an empty array so the
 * caller cannot accidentally draw a heading over nothing.
 */
export function OpportunityGrid({
  opportunities,
  onSave,
  savingKinds = [],
}: {
  opportunities: readonly Opportunity[];
  /**
   * What to do when a save control is pressed. Optional so the grid stays a pure
   * function of its props for a probe that only cares about words: a tile whose
   * action is a save still draws its button without one, and pressing it does
   * nothing rather than throwing.
   */
  onSave?: (kind: OpportunityKind) => void;
  /**
   * The tiles whose saves are in flight — a SET, not one kind.
   *
   * Two of these are two independent writes over two different populations, and
   * the first draft's single `savingKind` forced a choice between two wrong
   * answers: disable the other tiles, which is a lie about coupling, or leave
   * them pressable over a handler that silently returns, which is worse than
   * disabling because the control still looks live.
   *
   * A prop rather than state inside the card so the busy render is a render a
   * probe can produce, and empty — never `undefined` — because "nothing is
   * saving" is a fact, not a missing one.
   */
  savingKinds?: readonly OpportunityKind[];
}) {
  if (opportunities.length === 0) return null;

  return (
    <ul data-role="opportunity-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {opportunities.map((one) => (
        <OpportunityTile
          key={one.kind}
          opportunity={one}
          onSave={onSave}
          saving={savingKinds.includes(one.kind)}
        />
      ))}
    </ul>
  );
}

/**
 * What a press DOES — the whole chain, as a function a suite can call.
 *
 * ## WHY THIS IS NOT A METHOD ON THE CARD
 *
 * It was, and the four most interesting facts about it were covered by nothing.
 * `renderToStaticMarkup` runs no handlers, so a handler living inside
 * {@link OpportunitiesCard} is reachable only by rendering its *consequences*
 * separately — a busy grid, a note component, a predicate — and then asserting
 * over the file's own source text that the pieces are wired to each other. Two
 * `reload()` calls were counted with a regex. **A search for a string in the same
 * commit that wrote it proves the string is there and nothing else**; it passes
 * on a `reload()` in the wrong branch, on one inside a condition that is never
 * true, and on both of them left behind after the call they belonged to moved.
 *
 * Lifted out, every step is observable: the re-entrancy test, the in-flight set
 * going on and coming off, the request that actually leaves, the outcome, and the
 * re-read on *both* paths. `verify-dashboard` seeds `lib/config.ts` into the
 * require cache, replaces `fetch` with a recorder that opens no socket, and calls
 * this with the real `saveOpportunityCohort` — so the path, the method, the body,
 * the response contract and the decoding of a refusal are all the app's, not a
 * fixture's.
 *
 * The React parts stay in the component, which is why this takes setters rather
 * than owning state: a function that called `useState` would be a hook and
 * unrunnable again.
 *
 * ## `save` IS A PARAMETER BECAUSE REACHING THE CLIENT IS THE CARD'S PROBLEM
 *
 * Not a seam cut for a test. `lib/api/overview` reaches `lib/config.ts`, which
 * reads `import.meta.env` and exists only inside Vite, so the card reaches it
 * through a dynamic `await import` inside its own handler — and **that one line
 * is the only thing in this chain no suite in this repo can execute.** A dynamic
 * import escapes the require cache: seeding `lib/config.ts` there does not reach
 * it, which was measured rather than assumed. So the line stays where the grep
 * can see it and the state machine takes the function, which a suite can hand it
 * for real. What is left un-run is one module specifier, and it is named here
 * rather than covered by a search pretending to be a test.
 *
 * ## THE RE-READ IS IN A `finally`, AND THAT IS THE POINT OF THE SHAPE
 *
 * Success and refusal both re-read, and the refusal is the important half. The
 * one a reader is most likely to meet is *"There is nobody in this cohort any
 * more"*, which means the headline two lines above the button — "1,284 people
 * have not heard from you in 90 days" — is now false. Leaving the tile there
 * would leave a confident wrong number on the page, which is the defect this repo
 * has shipped twice. Written as two copies, one per branch, the failure path's
 * copy is exactly the one a later edit drops; written once it cannot be dropped
 * from one branch only.
 *
 * It can drop the LAST row, which is why the card no longer returns `null` while
 * it has an outcome to report; see {@link opportunitiesCardDraws}.
 */
export async function pressCohortSave({
  kind,
  inFlight,
  setInFlight,
  setOutcome,
  reload,
  save,
}: {
  /** The tile whose control was pressed. */
  kind: OpportunityKind;
  /**
   * The saves already running. A press on one of these returns without writing.
   *
   * `Button loading` already sets `disabled`, so a second press on the same tile
   * cannot reach here through the UI — the test is the rule, and a disabled
   * attribute is a rendering of it. A press on a DIFFERENT tile is allowed
   * through: it is a write over another population, and blocking it would leave
   * a live-looking control doing nothing.
   */
  inFlight: readonly OpportunityKind[];
  /** Adds the kind for the duration of the write, and takes it off after. */
  setInFlight: (next: (current: readonly OpportunityKind[]) => readonly OpportunityKind[]) => void;
  /** The one slot both outcomes share. `null` clears the previous verdict. */
  setOutcome: (outcome: CohortSaveOutcome | null) => void;
  /** Re-reads the tiles. Called on success and on refusal alike. */
  reload: () => void;
  /**
   * Writes the cohort and answers with the segment that now holds it.
   *
   * The card passes `saveOpportunityCohort`, reached through a dynamic import in
   * its own handler; see the header above for why that import is the caller's
   * job and not this function's.
   */
  save: (kind: OpportunityKind) => Promise<SaveCohortResponse>;
}): Promise<void> {
  if (inFlight.includes(kind)) return;

  setInFlight((current) => [...current, kind]);
  setOutcome(null);

  try {
    const saved = await save(kind);
    /*
     * `saved.created` is NOT read, and that is the design rather than an
     * omission. A second press on an unchanged population answers with the
     * segment already stored; the person's next step is the same either way, and
     * a flag the screen interprets is a sentence waiting to be written.
     */
    setOutcome({ ok: true, segmentId: saved.segment.id, name: saved.segment.name });
  } catch (failure) {
    /*
     * The server's own sentence, verbatim. Every refusal this endpoint makes is
     * written in `COHORT_REFUSALS` — "There is nobody in this cohort any more",
     * "Only an audience can become a segment" — and re-wording one here would
     * put two versions of the same refusal in the product.
     */
    setOutcome({ ok: false, kind, message: errorMessage(failure) });
  } finally {
    setInFlight((current) => current.filter((one) => one !== kind));
    reload();
  }
}

/**
 * Whether the card is on the page at all.
 *
 * Exported, and the reason is that it cannot be asserted any other way. A save's
 * outcome is state inside the card, `renderToStaticMarkup` runs no handler, and
 * the rule this encodes only matters after a press — so the alternative to one
 * exported predicate is a rule nothing checks.
 *
 * The rule: nothing to suggest and no failed read draws NOTHING, which is the
 * card's oldest property and the reason a well-run workspace does not watch this
 * panel appear and vanish. **An outcome is the third reason to draw**, and it was
 * missing. A refused save re-reads, a refusal of *"there is nobody in this cohort
 * any more"* can drop the last row, and the card would then have unmounted
 * taking its own refusal with it — the reader would have pressed a button and
 * watched the page go quiet. The verdict of a press stays until the next press.
 */
export function opportunitiesCardDraws({
  hasTiles,
  failedOutright,
  hasOutcome,
}: {
  /** The read answered with at least one thing worth doing. */
  hasTiles: boolean;
  /** The read failed and left nothing on screen, so there is a retry to offer. */
  failedOutright: boolean;
  /** A save of this reader's has something to report. */
  hasOutcome: boolean;
}): boolean {
  return hasTiles || failedOutright || hasOutcome;
}

/**
 * The card.
 *
 * Renders nothing while loading and nothing when there is nothing to suggest —
 * see the header and {@link opportunitiesCardDraws}. It draws without tiles in
 * two cases: a failed read, and a save whose verdict is still worth reporting.
 *
 * SLICE 65: "SEE ALL ON TODAY", AND ONLY FOR A WORKSPACE THAT HAS SENSE
 * -----------------------------------------------------------------------------
 * Sense's Today queue (slice 62) ranks these same opportunities beside the
 * findings this card cannot make — pressure, holdouts — by rupees at stake.
 * So on a workspace with Sense this card is a preview of a longer list, and it
 * says where the rest is, in one line under the grid.
 *
 * It is a link inside this card's own chrome rather than a card of its own:
 * the Overview is the approved design, and a "Today" panel beside this one
 * would be the same answer — what to do next — drawn twice.
 *
 * `seeAllOnToday` is decided by `HomePage` from `GET /overview`'s pillar rows,
 * the same verdict the sidebar draws Sense on, and defaults to off. A
 * workspace without Sense is not shown a link into a pillar its sidebar does
 * not offer (slice 59: an unentitled Sense is absent, never advertised). It
 * draws only beside tiles: with nothing to suggest the card is not on the
 * page, and a failed read is a retry, not a place to send somebody onward.
 */
export function OpportunitiesCard({
  opportunities,
  seeAllOnToday = false,
}: {
  opportunities: AsyncData<OpportunitiesResponse>;
  /** The workspace has Sense, so the full queue is one link away. */
  seeAllOnToday?: boolean;
}) {
  const { data, error, status, reload } = opportunities;

  /*
   * THE SAVE'S STATE, AND THE ONE THING IT DELIBERATELY DOES NOT KEEP.
   *
   * These two are all the card owns; what a press does with them is
   * {@link pressCohortSave}, out here as a function so it can be run rather than
   * described.
   *
   * There is no local copy of the tiles. After a save the press calls `reload()`,
   * so the control that replaces the button — `Create a campaign`, now carrying
   * the new segment's id — comes from the API that stored it rather than from
   * this component deciding a snapshot is current. That decision needs the count
   * as it is *now*, which only the server has: the API draws the link arm when a
   * cohort of that kind exists for today AND its `uniqueRecipients` still equals
   * today's count, and neither half of that is knowable here.
   *
   * The outcome line survives that reload as component state and is cleared by
   * the next press, because it is the one moment the segment's name is put in
   * front of the person and it links to the page that can rename it.
   */
  const [savingKinds, setSavingKinds] = useState<readonly OpportunityKind[]>([]);
  const [outcome, setOutcome] = useState<CohortSaveOutcome | null>(null);

  const failedOutright = status === 'error' && data === null;
  const hasTiles = data !== null && data.opportunities.length > 0;

  // Nothing to say, nothing to retry and no press to report: take up no space.
  if (!opportunitiesCardDraws({ hasTiles, failedOutright, hasOutcome: outcome !== null })) {
    return null;
  }

  return (
    <Card data-role="opportunities-card">
      {/* The Overview's own card chrome — 18px of padding, 0.95rem title. The
          argument, and why it is written out here rather than made the default
          in `Card`, is in one place: HomePage.tsx's header, under THE OVERVIEW
          IS ONE CARD FAMILY. It is not restated here, because it was restated
          on two cards with a call-site count in it and the count was wrong on
          both. `verify-dashboard` asserts the chrome; this points at the why. */}
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]" sub="from your data">
          Worth doing next
        </CardTitle>
      </CardHeader>

      {failedOutright && (
        <CardContent className="p-[18px] pt-0">
          <ErrorState
            error={error}
            title="Could not work out what is worth doing next"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-6"
          />
        </CardContent>
      )}

      {(hasTiles || outcome !== null) && (
        <CardContent className="flex flex-col gap-3 p-[18px] pt-0">
          {/*
            A refresh that failed over tiles still on screen says so in three
            words. The card's own title sits just above it, so the longer version
            — "Could not refresh what is worth doing next" — put the same noun
            phrase on the card twice while the tiles below stayed readable.
          */}
          {status === 'error' && data !== null && (
            <ErrorState
              error={error}
              title="Could not refresh"
              onRetry={reload}
              className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-4"
            />
          )}

          <OpportunityGrid
            opportunities={data?.opportunities ?? []}
            onSave={(kind) => {
              void pressCohortSave({
                kind,
                inFlight: savingKinds,
                setInFlight: setSavingKinds,
                setOutcome,
                reload,
                /*
                  The API client is reached HERE, inside the handler, and not at
                  the top of the file: `lib/api/overview` reaches `lib/config.ts`,
                  which reads `import.meta.env` and does not exist under
                  `renderToStaticMarkup`. A static import would make this module
                  unmountable by the two suites that assert everything on this
                  card. The cost is one fetch the first time somebody presses,
                  long after the app has booted — the same trade `CalendarCard`'s
                  `onAdd` makes, for the same reason.
                */
                save: async (one) => {
                  const { saveOpportunityCohort } = await import('../../lib/api/overview');
                  return saveOpportunityCohort(one);
                },
              });
            }}
            savingKinds={savingKinds}
          />

          {/*
            The save's verdict, in one slot, above the qualifier. A four-up grid
            that reflowed while somebody was scanning it would be worse than a
            line two inches lower, and one live region per card is what a screen
            reader wants rather than four announcing from wherever focus is.
          */}
          <CohortSaveNote outcome={outcome} />

          {/* Said once, under the grid, rather than inside all four tiles — and
              only where there are counts for it to qualify. A note about who is
              left out of a figure, printed with no figure on the card, is a
              sentence about nothing. */}
          {hasTiles && (
            <p className="text-xs leading-relaxed text-muted">{OPPORTUNITY_SUPPRESSION_NOTE}</p>
          )}

          {hasTiles && seeAllOnToday && (
            <Link
              to="/sense/today"
              className="self-start text-[0.82rem] text-text underline underline-offset-4"
              data-role="opportunities-today"
            >
              See all on Today
            </Link>
          )}
        </CardContent>
      )}
    </Card>
  );
}
